import { rangePickerFormItemProps } from "@/utils/componentUtils";
import { skipInitialFormValues } from "@/utils/formUtils";
import { URLQuery } from "@/utils/queryUtils";
import { validationConstants, validations } from "@/utils/validationUtils";
import { Button, Card, DatePicker, Flex, Form, Input, Select } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import ActionButton from "../../../../../common/components/buttons/ActionButton";
import AntIcon from "../../../../../common/components/icons/AntIcon";
import { rowGutter } from "../../../../../common/constants";
import { AgentCommonFilterFields } from "../../../../agent/components/commonfilters/AgentCommonFilterFields";
import { AgentExtendedFilterModalForm } from "../../../../agent/components/commonfilters/AgentExtendedFilterModalForm";
import { DEFAULT_SPECIAL_COMMISSIONS_REPORT_QUERY_PARAMS } from "../../api";
import { SpecialCommissionsReportScope } from "../../enums";
import { SpecialCommissionsReportFilterPageRequest } from "../../types";

type ExtendedFilterFormType = SpecialCommissionsReportFilterPageRequest & {
  periodDateRange: [string, string];
  completedEducationDateRange?: string[];
  completedAcademyDateRange?: string[];
};

type Props = {
  searchParams: SpecialCommissionsReportFilterPageRequest;
};

export const SpecialCommissionsReportFilterForm = ({ searchParams }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [form] = Form.useForm<ExtendedFilterFormType>();

  useEffect(() => {
    form.setFieldsValue({
      ...searchParams,
      periodDateRange:
        searchParams.periodStartDate && searchParams.periodEndDate
          ? [searchParams.periodStartDate, searchParams.periodEndDate]
          : undefined,
      completedEducationDateRange:
        searchParams.completedEducationDateMin && searchParams.completedEducationDateMax
          ? [searchParams.completedEducationDateMin, searchParams.completedEducationDateMax]
          : undefined,
      completedAcademyDateRange:
        searchParams.completedAcademyDateMin && searchParams.completedAcademyDateMax
          ? [searchParams.completedAcademyDateMin, searchParams.completedAcademyDateMax]
          : undefined,
    });
  }, [searchParams, form]);

  const handleFilterSubmit = ({
    periodDateRange,
    completedEducationDateRange,
    completedAcademyDateRange,
    ...filter
  }: ExtendedFilterFormType) => {
    const searchParams = {
      ...filter,
      periodStartDate: periodDateRange?.[0],
      periodEndDate: periodDateRange?.[1],
    };

    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            ...searchParams,
            ...skipInitialFormValues(searchParams, DEFAULT_SPECIAL_COMMISSIONS_REPORT_QUERY_PARAMS),
            pageIndex: undefined,
          },
        }),
      },
      { replace: true },
    );
  };

  return (
    <Card
      className="card-box"
      title={<h2>{t("commissions.special.titles.reportPage")}</h2>}
      extra={
        <ActionButton
          color="danger"
          size="middle"
          variant="outlined"
          icon="reload"
          onClick={() => {
            form.resetFields();
            handleFilterSubmit(form.getFieldsValue());
          }}
        >
          {t("common.resetFilter")}
        </ActionButton>
      }
    >
      <Form form={form} onFinish={handleFilterSubmit} layout="vertical" name="specialCommissionsReportForm">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(4, 1fr)`,
            gap: rowGutter,
            alignItems: "end",
          }}
          className="min-width-0-children"
        >
          <AgentCommonFilterFields>
            <Form.Item
              name="periodDateRange"
              label={t("commissions.special.filter.periodDateRange")}
              rules={[validations.none]}
              {...rangePickerFormItemProps("month")}
            >
              <DatePicker.RangePicker
                style={{ width: "100%" }}
                format="MMMM YYYY"
                maxDate={dayjs()}
                placeholder={[t("common.from"), t("common.to")]}
                picker="month"
              />
            </Form.Item>
          </AgentCommonFilterFields>

          <Form.Item
            name="scope"
            label={t("commissions.special.filter.scope")}
            rules={[validations.none]}
            initialValue={SpecialCommissionsReportScope.PAYER}
          >
            <Select
              options={Object.values(SpecialCommissionsReportScope).map((item) => ({
                value: item,
                label: t(`commissions.special.enums.reportScope.${item}`),
              }))}
            />
          </Form.Item>

          <Form.Item
            name="specialCommissionKeyword"
            label={t("commissions.special.filter.specialCommissionKeyword")}
            rules={[
              validations.size(
                validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
                validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
              ),
            ]}
          >
            <Input allowClear placeholder={t("commissions.special.helpers.specialCommissionKeywordHint")} />
          </Form.Item>
        </div>

        <Flex justify="space-between">
          <AgentExtendedFilterModalForm
            onSubmit={(values) => {
              form.setFieldsValue(values);
              handleFilterSubmit(form.getFieldsValue());
            }}
          />

          <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
            {t("common.filter")}
          </Button>
        </Flex>
      </Form>
    </Card>
  );
};
